import axios from "axios";

/**
 * Check session status from API with timeout
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000ms)
 * @returns {Promise<Object>} Session status object
 */
export const checkSession = async (timeoutMs = 5000) => {
  try {
    const base = (process.env.REACT_APP_API_ORI || "").replace(/\/$/, "");
    const url = `${base}/verifysession/check_session`;
    
    // Create a promise that rejects after the timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Session check timeout'));
      }, timeoutMs);
    });

    // Create the actual request promise
    const requestPromise = axios.get(url, {
      withCredentials: true // Ini akan mengirim cookie browser ke server Code Igniter
    });

    // Race the timeout against the actual request
    const response = await Promise.race([requestPromise, timeoutPromise]);

    const isLoggedIn = response?.data?.isLoggedIn === true;

    return {
      isValid: isLoggedIn,
      message: isLoggedIn ? "Logged in" : "Not logged in",
      data: response?.data || null,
    };
  } catch (error) {
    console.error("Session check error:", error.message || error);
    return {
      isValid: false,
      message: error.message === 'Session check timeout' ? "Session check timeout" : "Session check failed",
      data: null,
    };
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

/**
 * Clear session data
 */
export const clearSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("userData");
  localStorage.removeItem("sessionCheckFlag"); // Reset session check flag
  // Add other session-related data cleanup here
};

/**
 * Set session data
 * @param {string} token - Authentication token
 * @param {Object} userData - User data
 */
export const setSession = (token, userData = null) => {
  localStorage.setItem("token", token);
  if (userData) {
    localStorage.setItem("userData", JSON.stringify(userData));
  }
  // Reset session check flag ketika set session baru
  localStorage.removeItem("sessionCheckFlag");
};

/**
 * Reset session check status - untuk force check ulang
 */
export const resetSessionCheck = () => {
  // Clear session check flag dari localStorage
  localStorage.removeItem("sessionCheckFlag");
};

/**
 * Check session with loading indicator
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000ms)
 * @returns {Promise<Object>} Session status object with loading state
 */
export const checkSessionWithLoading = async (timeoutMs = 5000) => {
  // Set loading state in localStorage to indicate session check is in progress
  localStorage.setItem("sessionCheckLoading", "true");
  
  try {
    const result = await checkSession(timeoutMs);
    // Clear loading state
    localStorage.removeItem("sessionCheckLoading");
    return result;
  } catch (error) {
    // Clear loading state even if there's an error
    localStorage.removeItem("sessionCheckLoading");
    throw error;
  }
};
